import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { marked } from 'marked';
import { saveAs } from 'file-saver';
import { BackendService } from '../../services/general/backend.service';
import { UserService } from '../../services/user.service';
import { ProjectsService } from '../../services/projects.service';
import { ModalNerfAllDataComponent } from '../../modals/modal-nerf-all-data/modal-nerf-all-data.component';

/** Metrica chiave confrontata nel modale "Confronta simulazioni". */
interface ComparisonMetric {
  key: string;
  labelKey: string;
  unit: string;
  decimals: number;
}

@Component({
    selector: 'app-nerf-simulator',
    templateUrl: './nerf-simulator.component.html',
    styleUrls: ['./nerf-simulator.component.scss'],
    standalone: false
})
export class NerfSimulatorComponent implements OnInit, OnDestroy {
  @Input() projectId: any;

  public simulatorForm: FormGroup;
  public loading = false;
  public simulationResult: any = null;
  public hasResult = false;
  public simulationHistory: any[] = [];
  public loadingHistory = false;
  public savingSimulation = false;
  public currentSimulationId: number | null = null;
  public currentSimulationCreatedAt: string | null = null;
  public isSimulationSaved = false;

  /** Centri di riferimento caricati da nerf-simulator-records (combo in cima al form). */
  public cityRecords: any[] = [];
  public loadingCities = false;
  /** Combo selezione centro: nessuna selezione => form azzerato e disabilitato. */
  public cityControl = new FormControl<number | null>(null);
  /** Centri filtrati dalla ricerca testuale dentro la combo. */
  public filteredCityRecords: any[] = [];
  /** Input di ricerca dentro il pannello della combo (per l'autofocus all'apertura). */
  @ViewChild('citySearchInput') citySearchInput?: ElementRef<HTMLInputElement>;

  /** Colonne tabella storico (mat-table + table-primary come nel resto dell'app) */
  readonly historyDisplayedColumns: string[] = [
    'select',
    'created_at',
    'city',
    'rotation_index',
    'cost_per_patient_year',
    'actions',
  ];

  /** Id delle simulazioni selezionate per la generazione del report. */
  public selectedSimulationIds = new Set<number>();
  /** Se true la tabella storico mostra tutte le simulazioni, altrimenti solo quelle del centro selezionato. */
  public showAllSimulations = false;
  /** Report in corso di generazione (spinner sul pulsante). */
  public generatingReport = false;
  /** Ultimo report generato (status/risultato mostrati sotto la tabella). */
  public currentReport: any = null;
  private reportPollTimer: any = null;

  @ViewChild('helpDialog') helpDialog!: TemplateRef<any>;
  /** Scheda di cui il modale di guida mostra titolo e testo. */
  public helpSection: 'editable' | 'fixed' | 'costs' = 'editable';

  @ViewChild('comparisonDialog') comparisonDialog!: TemplateRef<any>;
  /** Grafico scelto nel modale di confronto (pulsanti Grafico 1…5). */
  public comparisonChartType = 1;
  public readonly comparisonChartTypes: ReadonlyArray<number> = [1, 2, 3, 4, 5];
  /** Grafico 1: un grafico per metrica chiave, costruiti all'apertura del modale. */
  public comparisonCharts: { labelKey: string; unit: string; options: any }[] = [];
  /** Grafici 2–5: opzioni ECharts per tipo di grafico. */
  public comparisonAltCharts: Record<number, any> = {};

  /**
   * Metriche confrontate nel modale, con unità e decimali. Sottoinsieme del pannello
   * "Metriche chiave": costo totale annuo e costo medio mix restano nel pannello ma
   * non nei grafici di confronto.
   */
  private readonly comparisonMetrics: ReadonlyArray<ComparisonMetric> = [
    { key: 'patients_per_month', labelKey: 'components.nerf-simulator.patients_per_month', unit: '', decimals: 0 },
    { key: 'avg_infusion_time_mix', labelKey: 'components.nerf-simulator.avg_infusion_time_mix', unit: 'min', decimals: 1 },
    { key: 'cost_per_patient_year', labelKey: 'components.nerf-simulator.cost_per_patient_year', unit: 'EUR', decimals: 2 },
  ];

  /**
   * Tooltip dei grafici di confronto: `confine` lo tiene dentro il riquadro del grafico
   * (nel grafico 1 i riquadri sono affiancati e il tooltip usciva tagliato a sinistra),
   * il CSS manda a capo le composizioni lunghe invece di allargarlo a dismisura.
   */
  private readonly comparisonTooltipBase = {
    confine: true,
    extraCssText: 'max-width: 320px; white-space: normal;',
  };

  /** Un colore per simulazione, uguale in tutti i grafici (palette del simulatore). */
  private readonly comparisonColors: ReadonlyArray<string> = [
    '#7b55c8', '#1ba19a', '#e67e22', '#6945af', '#e57373', '#27ae60', '#ffb74d', '#9169db',
  ];

  /**
   * Valori di default solo per i campi modificabili in UI; i fissi/disabilitati restano quelli attuali (raw).
   * Campi con markup commentato nel template (nascosti all’utente ma ancora nel FormGroup):
   * Avg_Infusion_Min_Scenario, Gap_TimeHours_Per_Day, avg_patients_per_chair_per_day.
   */
  private readonly editableParamDefaults: Record<string, number> = {
    ocrelizumab_subcutaneous_composition: 40,
    ocrelizumab_intravenous_composition: 40,
    ublituximab_composition: 20,
    natalizumab_intravenous_composition: 0,
    natalizumab_subcutaneous_composition: 0,
    Gap_Min: 15,
    Safety_Buffer: 10,
    No_Show_Rate: 0,
    Nurses_per_Shift: 3,
    Patients_per_Nurse: 2,
    Chairs_Active: 5,
    number_of_opening_days: 20,
    infusion_service_hours: 8,

    // Cost parameters (editable)
    ocrelizumab_intravenous_cost_single_infusion: 2000,
    ocrelizumab_subcutaneous_cost_single_infusion: 3238,
    ublituximab_cost_single_infusion: 1800,
    natalizumab_intravenous_cost_single_infusion: 2000,
    natalizumab_subcutaneous_cost_single_infusion: 2000,
    materials_cost_single_infusion: 20,
    nurses_cost_for_hour: 0,
  };

  /**
   * Somministrazioni annue per terapia: parametri fissi letti da input_data del centro.
   * Valori usati se il centro non li ha ancora (gli stessi della migration 2026_09_14).
   */
  private readonly administrationsPerYearDefaults: Record<string, number> = {
    ocrelizumab_subcutaneous_administrations_per_year: 2,
    ocrelizumab_intravenous_administrations_per_year: 2,
    ublituximab_administrations_per_year: 2.1667,
    natalizumab_intravenous_administrations_per_year: 13,
    natalizumab_subcutaneous_administrations_per_year: 13,
  };

  constructor(
    private fb: FormBuilder,
    private backend: BackendService,
    private userService: UserService,
    private projectsService: ProjectsService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {
    this.simulatorForm = this.fb.group({
      // NB: non rimuovere i controlli il cui input è commentato in .html — servono ancora al backend (getRawValue).
      // Medication parameters (compositions stored as percentages for display)
      ocrelizumab_subcutaneous_min: [10, [Validators.required, Validators.min(0)]],
      ocrelizumab_subcutaneous_composition: [40, [Validators.required, Validators.min(0), Validators.max(100)]],
      ocrelizumab_intravenous_min: [180, [Validators.required, Validators.min(0)]],
      ocrelizumab_intravenous_composition: [40, [Validators.required, Validators.min(0), Validators.max(100)]],
      ublituximab_min: [60, [Validators.required, Validators.min(0)]],
      ublituximab_composition: [20, [Validators.required, Validators.min(0), Validators.max(100)]],
      natalizumab_intravenous_min: [30, [Validators.required, Validators.min(0)]],
      natalizumab_subcutaneous_min: [30, [Validators.required, Validators.min(0)]],
      natalizumab_intravenous_composition: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      natalizumab_subcutaneous_composition: [0, [Validators.required, Validators.min(0), Validators.max(100)]],

      // Working hours parameters
      Hours_Per_Day: [8, [Validators.required, Validators.min(0)]],
      Gap_Min: [15, [Validators.required, Validators.min(0)]],
      Working_Days_Month: [20, [Validators.required, Validators.min(0)]],
      Avg_Infusion_Min_Scenario: [156, [Validators.required, Validators.min(0)]],
      Safety_Buffer: [10, [Validators.required, Validators.min(0), Validators.max(100)]],
      No_Show_Rate: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      Gap_TimeHours_Per_Day: [2, [Validators.required, Validators.min(0)]],

      // Staff parameters
      Nurses_per_Shift: [3, [Validators.required, Validators.min(0)]],
      Patients_per_Nurse: [2, [Validators.required, Validators.min(0)]],

      // Capacity parameters
      avg_patients_per_chair_per_day: [2.470149, [Validators.required, Validators.min(0)]],
      Chairs_Active: [5, [Validators.required, Validators.min(0)]],
      number_of_opening_days: [20, [Validators.required, Validators.min(0)]],

      // Time parameters
      prep_time_chair_materials: [20, [Validators.required, Validators.min(0)]],
      patient_reception_assessment_time: [26, [Validators.required, Validators.min(0)]],
      vitals_premedication_setup_time: [28, [Validators.required, Validators.min(0)]],
      // Fixed per-drug post-infusion monitoring times (minutes). Read-only / not user-editable.
      post_infusion_surveillance_time_ocrelizumab_subcutaneous: [52, [Validators.min(0)]],
      post_infusion_surveillance_time_ocrelizumab_intravenous: [52, [Validators.min(0)]],
      post_infusion_surveillance_time_ublituximab: [52, [Validators.min(0)]],
      post_infusion_surveillance_time_natalizumab_intravenous: [60, [Validators.min(0)]],
      post_infusion_surveillance_time_natalizumab_subcutaneous: [60, [Validators.min(0)]],
      post_infusion_admin_time: [17, [Validators.required, Validators.min(0)]],
      avg_operational_gap_between_infusions: [19, [Validators.required, Validators.min(0)]],
      // Somministrazioni annue per terapia (fisse, da input_data del centro).
      ocrelizumab_subcutaneous_administrations_per_year: [2, [Validators.required, Validators.min(0)]],
      ocrelizumab_intravenous_administrations_per_year: [2, [Validators.required, Validators.min(0)]],
      ublituximab_administrations_per_year: [2.1667, [Validators.required, Validators.min(0)]],
      natalizumab_intravenous_administrations_per_year: [13, [Validators.required, Validators.min(0)]],
      natalizumab_subcutaneous_administrations_per_year: [13, [Validators.required, Validators.min(0)]],
      infusion_service_hours: [8, [Validators.required, Validators.min(0)]],

      // Cost parameters
      ocrelizumab_intravenous_cost_single_infusion: [2000, [Validators.required, Validators.min(0)]],
      ocrelizumab_subcutaneous_cost_single_infusion: [3238, [Validators.required, Validators.min(0)]],
      ublituximab_cost_single_infusion: [1800, [Validators.required, Validators.min(0)]],
      natalizumab_intravenous_cost_single_infusion: [2000, [Validators.required, Validators.min(0)]],
      natalizumab_subcutaneous_cost_single_infusion: [2000, [Validators.required, Validators.min(0)]],
      materials_cost_single_infusion: [20, [Validators.required, Validators.min(0)]],
      nurses_cost_for_hour: [0, [Validators.required, Validators.min(0)]],
    }, { validators: this.compositionSumValidator });
  }

  // Custom validator to ensure the sum of compositions is <= 100%
  private compositionSumValidator = (control: AbstractControl): ValidationErrors | null => {
    const rituximab = Number(control.get('ocrelizumab_subcutaneous_composition')?.value) || 0;
    const ocrelizumab = Number(control.get('ocrelizumab_intravenous_composition')?.value) || 0;
    const ublituximab = Number(control.get('ublituximab_composition')?.value) || 0;
    const natalizumabIntravenous = Number(control.get('natalizumab_intravenous_composition')?.value) || 0;
    const natalizumabSubcutaneous = Number(control.get('natalizumab_subcutaneous_composition')?.value) || 0;

    const sum = rituximab + ocrelizumab + ublituximab + natalizumabIntravenous + natalizumabSubcutaneous;

    // console.log('Composition Validator - Rituximab:', rituximab, 'Ocrelizumab:', ocrelizumab, 'Ublituximab:', ublituximab, 'Sum:', sum);

    // La somma delle composizioni deve essere esattamente 100% (tolleranza per errori floating point).
    if (Math.abs(sum - 100) > 0.001) {
      // console.log('Validation FAILED - Sum must equal 100%');
      return { compositionSumExceeded: { sum, max: 100 } };
    }

    // console.log('Validation PASSED');
    return null;
  }

  ngOnInit(): void {
    // Subscribe to composition field changes to trigger the sum validation.
    for (const compositionControl of this.compositionControls) {
      this.simulatorForm.get(compositionControl)?.valueChanges.subscribe(() => {
        this.simulatorForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      });
    }

    // Stato iniziale: tutti i campi a 0 e form disabilitato finché non si sceglie un centro.
    this.resetAllToZeroAndDisable();

    // Carica l'elenco dei centri per la combo di selezione.
    this.loadCityRecords();

    // Load simulation history
    this.loadSimulationHistory();
  }

  ngOnDestroy(): void {
    this.clearReportPoll();
  }

  /** Apre il modale di guida della scheda indicata. */
  public openHelp(section: 'editable' | 'fixed' | 'costs'): void {
    this.helpSection = section;
    this.dialog.open(this.helpDialog, {
      width: '550px',
      maxHeight: '90vh'
    });
  }

  /**
   * Confronto tra le simulazioni selezionate (almeno 2), nell'ordine in cui sono state
   * selezionate: la prima è il riferimento del grafico 3. Prepara i 5 grafici e apre il modale.
   */
  public openComparisonCharts(): void {
    const simulations: any[] = Array.from(this.selectedSimulationIds)
      .map((id) => this.simulationHistory.find((simulation: any) => simulation.id === id))
      .filter((simulation: any) => simulation?.output_results);
    if (simulations.length < 2) {
      return;
    }

    // Centro + data, perché lo stesso centro può comparire con mix diversi.
    const names: string[] = simulations.map((simulation: any) =>
      `${simulation.city || '-'}\n${this.formatComparisonDate(simulation.created_at)}`
    );
    const colors: string[] = simulations.map((_: any, index: number) =>
      this.comparisonColors[index % this.comparisonColors.length]
    );
    // values[m][s]: valore della metrica m per la simulazione s (null se assente).
    const values: (number | null)[][] = this.comparisonMetrics.map((metric) =>
      simulations.map((simulation: any) => {
        const value = simulation.output_results?.[metric.key];
        return value == null || isNaN(Number(value)) ? null : Number(value);
      })
    );

    this.comparisonCharts = this.comparisonMetrics.map((metric, m) => ({
      labelKey: metric.labelKey,
      unit: metric.unit,
      options: this.buildMetricBarChart(simulations, names, colors, metric, values[m]),
    }));
    const costIndex = this.comparisonMetrics.findIndex((metric) => metric.key === 'cost_per_patient_year');
    this.comparisonAltCharts = {
      2: this.buildNormalizedChart(names, colors, values),
      3: this.buildDeltaChart(names, colors, values),
      4: this.buildRadarChart(names, colors, values),
      5: this.buildMixCostChart(simulations, names, values[costIndex]),
    };

    // A tutto schermo: i grafici di confronto crescono con la finestra e le etichette
    // (centro + data sull'asse, nomi delle metriche) hanno lo spazio per restare leggibili.
    // Le misure stanno qui e non in una panelClass: MatDialog le applica inline al
    // pannello, senza bisogno di regole in un foglio di stile globale.
    this.dialog.open(this.comparisonDialog, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      autoFocus: false
    });
  }

  /** Apre il modale "Tutti i dati" (ModalNerfAllDataComponent) sulla simulazione corrente. */
  public openAllData(): void {
    if (!this.simulationResult) {
      return;
    }

    this.dialog.open(ModalNerfAllDataComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { result: this.simulationResult, createdAt: this.currentSimulationCreatedAt },
    });
  }

  /** Frecce del modale di confronto: grafico precedente (-1) o successivo (+1), in modo ciclico. */
  public showComparisonChart(step: number): void {
    const count = this.comparisonChartTypes.length;
    this.comparisonChartType = ((this.comparisonChartType - 1 + step) % count + count) % count + 1;
  }

  private formatComparisonDate(value: any): string {
    const date = value ? new Date(value) : null;
    return date && !isNaN(date.getTime())
      ? date.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
      : '';
  }

  /** Il tooltip di ECharts è HTML: il nome del centro arriva dai dati, va escapato. */
  private escapeHtml(text: string): string {
    const entities: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return (text || '').replace(/[&<>"']/g, (char) => entities[char]);
  }

  private metricLabel(metric: ComparisonMetric): string {
    return this.translateService.instant(metric.labelKey);
  }

  private formatNumber(value: any, decimals: number): string {
    return value == null || isNaN(Number(value))
      ? '-'
      : Number(value).toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }

  private formatMetric(value: any, metric: ComparisonMetric): string {
    const text = this.formatNumber(value, metric.decimals);
    return metric.unit && text !== '-' ? `${text} ${metric.unit}` : text;
  }

  /** Nome della simulazione su una riga (tooltip e legende). */
  private singleLine(name: string): string {
    return name.replace('\n', ' · ');
  }

  /**
   * Vero quando il valore arriva vicino all'estremo del grafico. Lì l'etichetta va
   * girata verso il centro: fuori dall'elemento finirebbe oltre il bordo e la
   * finestra la taglierebbe.
   */
  private isNearChartEdge(value: number | null, extreme: number): boolean {
    return extreme > 0 && value != null && Math.abs(value) >= extreme * 0.9;
  }

  /** Grafico 1: una barra per simulazione per la metrica indicata. */
  private buildMetricBarChart(simulations: any[], names: string[], colors: string[],
                              metric: ComparisonMetric, values: (number | null)[]): any {
    const max = Math.max(0, ...values.map((value) => value ?? 0));
    return {
      tooltip: {
        ...this.comparisonTooltipBase,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const index = params?.[0]?.dataIndex ?? 0;
          const summary = simulations[index]?.composition_summary;
          return this.escapeHtml(this.singleLine(names[index]))
            + (summary ? `<br/>${this.escapeHtml(summary)}` : '')
            + `<br/><b>${this.formatMetric(values[index], metric)}</b>`;
        },
      },
      // Margini in px e non in %: l'etichetta sopra la barra più alta va nello spazio
      // di `top`, che con una percentuale si riduce troppo sui grafici bassi e taglia il valore.
      grid: { left: 12, right: 20, bottom: 8, top: 48, containLabel: true },
      xAxis: {
        type: 'category',
        data: names,
        axisLabel: { interval: 0, width: 150, overflow: 'break', rotate: names.length > 6 ? 30 : 0 },
      },
      yAxis: { type: 'value' },
      series: [{
        type: 'bar',
        barMaxWidth: 48,
        data: values.map((value, index) => ({
          value,
          itemStyle: { color: colors[index] },
          // Barra più alta: etichetta dentro la barra (verso il basso) invece che sopra.
          label: this.isNearChartEdge(value, max)
            ? { position: 'insideTop', color: '#fff' }
            : { position: 'top' },
        })),
        label: { show: true, formatter: (params: any) => this.formatNumber(params.value, metric.decimals) },
      }],
    };
  }

  /** Grafico 2: tutte le metriche insieme, ciascuna in % del valore più alto tra le simulazioni. */
  private buildNormalizedChart(names: string[], colors: string[], values: (number | null)[][]): any {
    const labels = names.map((name) => this.singleLine(name));
    const maxima = values.map((row) => Math.max(0, ...row.map((value) => value ?? 0)));
    return {
      tooltip: {
        ...this.comparisonTooltipBase,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          const m = params?.[0]?.dataIndex ?? 0;
          const metric = this.comparisonMetrics[m];
          return `<b>${this.escapeHtml(this.metricLabel(metric))}</b>`
            + params.map((item: any) => `<br/>${item.marker}${this.escapeHtml(labels[item.seriesIndex])}: `
              + `${this.formatMetric(values[m][item.seriesIndex], metric)} (${this.formatNumber(item.value, 1)}%)`).join('');
        },
      },
      legend: { type: 'scroll', top: 0, data: labels },
      grid: { left: '3%', right: '4%', bottom: '3%', top: 48, containLabel: true },
      xAxis: {
        type: 'category',
        data: this.comparisonMetrics.map((metric) => this.metricLabel(metric)),
        axisLabel: { interval: 0, width: 150, overflow: 'break' },
      },
      yAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
      series: labels.map((label, s) => ({
        name: label,
        type: 'bar',
        barMaxWidth: 28,
        itemStyle: { color: colors[s] },
        data: values.map((row, m) => row[s] == null || !maxima[m]
          ? null
          : Math.round((row[s] as number) / maxima[m] * 1000) / 10),
      })),
    };
  }

  /** Grafico 3: scostamento % di ogni simulazione rispetto alla prima selezionata (riferimento). */
  private buildDeltaChart(names: string[], colors: string[], values: (number | null)[][]): any {
    const labels = names.map((name) => this.singleLine(name));
    const signed = (value: any): string => value == null ? '-' : `${value > 0 ? '+' : ''}${this.formatNumber(value, 1)}%`;
    const delta = (row: (number | null)[], s: number): number | null => {
      const reference = row[0];
      const value = row[s];
      return reference && value != null ? Math.round((value - reference) / Math.abs(reference) * 1000) / 10 : null;
    };
    // Calcolati una volta sola: servono sia come dati delle serie sia per sapere
    // quali barre arrivano al bordo e vanno etichettate verso il centro.
    const deltas: (number | null)[][] = labels.slice(1).map((_label, i) => values.map((row) => delta(row, i + 1)));
    const maxAbsDelta = Math.max(0, ...deltas.flat().map((value) => value == null ? 0 : Math.abs(value)));
    return {
      tooltip: {
        ...this.comparisonTooltipBase,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          const m = params?.[0]?.dataIndex ?? 0;
          const metric = this.comparisonMetrics[m];
          return `<b>${this.escapeHtml(this.metricLabel(metric))}</b>`
            + `<br/>${this.escapeHtml(labels[0])}: ${this.formatMetric(values[m][0], metric)}`
            + params.map((item: any) => `<br/>${item.marker}${this.escapeHtml(labels[item.seriesIndex + 1])}: `
              + `${this.formatMetric(values[m][item.seriesIndex + 1], metric)} (${signed(item.value)})`).join('');
        },
      },
      legend: { type: 'scroll', top: 0, data: labels.slice(1) },
      // `right` in px per lasciare spazio alle etichette fuori dalla barra: quelle delle
      // barre più lunghe passano comunque dentro (vedi `data` delle serie).
      grid: { left: 12, right: 80, bottom: 8, top: 48, containLabel: true },
      xAxis: { type: 'value', axisLabel: { formatter: '{value}%' } },
      yAxis: {
        type: 'category',
        inverse: true,
        data: this.comparisonMetrics.map((metric) => this.metricLabel(metric)),
        axisLabel: { width: 220, overflow: 'break' },
      },
      series: labels.slice(1).map((label, i) => ({
        name: label,
        type: 'bar',
        barMaxWidth: 18,
        itemStyle: { color: colors[i + 1] },
        // Barre firmate: l'etichetta sta dalla parte in cui cresce la barra, e sulle
        // barre più lunghe passa dentro, verso il centro, per non uscire dal grafico.
        data: deltas[i].map((value) => ({
          value,
          label: this.isNearChartEdge(value, maxAbsDelta)
            ? { position: (value ?? 0) >= 0 ? 'insideRight' : 'insideLeft', color: '#fff' }
            : { position: (value ?? 0) >= 0 ? 'right' : 'left' },
        })),
        label: { show: true, formatter: (params: any) => signed(params.value) },
      })),
    };
  }

  /** Grafico 4: radar, un poligono per simulazione sulle metriche chiave (scala sul valore più alto). */
  private buildRadarChart(names: string[], colors: string[], values: (number | null)[][]): any {
    const labels = names.map((name) => this.singleLine(name));
    return {
      tooltip: {
        ...this.comparisonTooltipBase,
        trigger: 'item',
        formatter: (params: any) => `<b>${this.escapeHtml(labels[params.dataIndex])}</b>`
          + this.comparisonMetrics.map((metric, m) =>
            `<br/>${this.escapeHtml(this.metricLabel(metric))}: ${this.formatMetric(values[m][params.dataIndex], metric)}`).join(''),
      },
      legend: { type: 'scroll', bottom: 0, data: labels },
      radar: {
        // I nomi delle metriche stanno fuori dal poligono: raggio più stretto e testo
        // mandato a capo, altrimenti quelli a sinistra e a destra escono dal grafico.
        radius: '55%',
        center: ['50%', '52%'],
        axisName: { width: 150, overflow: 'break', lineHeight: 16 },
        indicator: this.comparisonMetrics.map((metric, m) => ({
          name: this.metricLabel(metric),
          max: (Math.max(0, ...values[m].map((value) => value ?? 0)) * 1.1) || 1,
        })),
      },
      series: [{
        type: 'radar',
        data: labels.map((label, s) => ({
          name: label,
          // '-' = valore assente (es. simulazioni salvate prima dei costi annui): ECharts salta il punto.
          value: values.map((row) => row[s] ?? '-'),
          itemStyle: { color: colors[s] },
          lineStyle: { color: colors[s] },
          areaStyle: { color: colors[s], opacity: 0.12 },
        })),
      }],
    };
  }

  /** Grafico 5: mix terapeutico per simulazione (barre impilate, %) e costo per paziente (linea, asse destro). */
  private buildMixCostChart(simulations: any[], names: string[], costs: (number | null)[]): any {
    const costMetric = this.comparisonMetrics.find((metric) => metric.key === 'cost_per_patient_year') as ComparisonMetric;
    const maxCost = Math.max(0, ...costs.map((cost) => cost ?? 0));
    const drugColors = ['#7b55c8', '#1ba19a', '#e67e22', '#6945af', '#e57373'];
    const drugs = this.compositionShortNames
      .map(([key, name], d) => ({
        name,
        color: drugColors[d % drugColors.length],
        data: simulations.map((simulation: any) =>
          Math.round((Number(this.parseInputParameters(simulation.input_parameters)?.[key]) || 0) * 1000) / 10),
      }))
      .filter((drug) => drug.data.some((value) => value > 0));
    return {
      tooltip: {
        ...this.comparisonTooltipBase,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          const index = params?.[0]?.dataIndex ?? 0;
          return `<b>${this.escapeHtml(this.singleLine(names[index]))}</b>`
            + params.map((item: any) => `<br/>${item.marker}${this.escapeHtml(item.seriesName)}: `
              + (item.seriesType === 'line'
                ? this.formatMetric(costs[index], costMetric)
                : `${this.formatNumber(item.value, 1)}%`)).join('');
        },
      },
      legend: { type: 'scroll', top: 0 },
      // `top` più largo della legenda: la linea dei costi porta l'etichetta sopra il punto,
      // e sul punto più alto finirebbe sotto la legenda o fuori dal grafico.
      grid: { left: 12, right: 20, bottom: 8, top: 68, containLabel: true },
      xAxis: {
        type: 'category',
        data: names,
        axisLabel: { interval: 0, width: 150, overflow: 'break', rotate: names.length > 6 ? 30 : 0 },
      },
      yAxis: [
        { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
        { type: 'value', axisLabel: { formatter: '{value} €' }, splitLine: { show: false } },
      ],
      series: [
        ...drugs.map((drug) => ({
          name: drug.name,
          type: 'bar',
          stack: 'mix',
          barMaxWidth: 48,
          itemStyle: { color: drug.color },
          data: drug.data,
        })),
        {
          name: this.metricLabel(costMetric),
          type: 'line',
          yAxisIndex: 1,
          symbolSize: 8,
          itemStyle: { color: '#333' },
          lineStyle: { color: '#333' },
          // Sul punto più alto l'etichetta va sotto il punto, verso il centro:
          // sopra finirebbe contro la legenda o fuori dal grafico.
          data: costs.map((cost) => ({
            value: cost,
            label: { position: this.isNearChartEdge(cost, maxCost) ? 'bottom' : 'top' },
          })),
          label: { show: true, formatter: (params: any) => this.formatNumber(params.value, 0) },
        },
      ],
    };
  }

  /**
   * Campi non editabili: read-only in UI e/o input commentati nel template ma ancora inviati al backend.
   * Va richiamato dopo enable() perché enable() riabilita tutti i controlli.
   */
  private applyFixedFieldState(): void {
    const fixedControls = [
      'ocrelizumab_subcutaneous_min',
      'ocrelizumab_intravenous_min',
      'ublituximab_min',
      'natalizumab_intravenous_min',
      'natalizumab_subcutaneous_min',
      'Avg_Infusion_Min_Scenario',
      'Gap_TimeHours_Per_Day',
      'avg_patients_per_chair_per_day',
      'prep_time_chair_materials',
      'patient_reception_assessment_time',
      'vitals_premedication_setup_time',
      'post_infusion_surveillance_time_ocrelizumab_subcutaneous',
      'post_infusion_surveillance_time_ocrelizumab_intravenous',
      'post_infusion_surveillance_time_ublituximab',
      'post_infusion_surveillance_time_natalizumab_intravenous',
      'post_infusion_surveillance_time_natalizumab_subcutaneous',
      'post_infusion_admin_time',
      'avg_operational_gap_between_infusions',
      'ocrelizumab_subcutaneous_administrations_per_year',
      'ocrelizumab_intravenous_administrations_per_year',
      'ublituximab_administrations_per_year',
      'natalizumab_intravenous_administrations_per_year',
      'natalizumab_subcutaneous_administrations_per_year',
    ];
    for (const name of fixedControls) {
      this.simulatorForm.get(name)?.disable({ emitEvent: false });
    }
  }

  /** Azzera tutti i controlli del form e disabilita l'intero form (stato di default). */
  private resetAllToZeroAndDisable(): void {
    const zeroed: Record<string, number> = {};
    Object.keys(this.simulatorForm.controls).forEach((name) => (zeroed[name] = 0));
    this.simulatorForm.reset(zeroed, { emitEvent: false });
    this.simulatorForm.disable({ emitEvent: false });
  }

  /** Vero per le organizzazioni "hospital" (customs.profile = 'simulator'). */
  private get isSimulatorProfile(): boolean {
    return this.userService?.currentOrganization?.customs?.profile === 'simulator';
  }

  /**
   * Carica i centri di riferimento per popolare la combo "center of interest".
   * Per le organizzazioni simulator, la combo mostra gli hospital (project) a cui
   * l'utente loggato è stato invitato, non l'elenco globale nerf-simulator-records.
   */
  loadCityRecords(): void {
    this.loadingCities = true;

    const handleResponse = (response: any) => {
      const records = response?.records ?? response?.data ?? response ?? [];
      // Ordina i centri per nome (case-insensitive) nella combo.
      this.cityRecords = [...records].sort((a: any, b: any) =>
        (a?.city ?? '').localeCompare(b?.city ?? '', undefined, { sensitivity: 'base' })
      );
      this.filteredCityRecords = [...this.cityRecords];
      this.loadingCities = false;

      // All'apertura seleziona automaticamente il centro marcato come default (select_default = 1).
      this.selectDefaultCity();
    };

    const handleError = (error: any) => {
      console.error('Error loading center-of-interest records:', error);
      this.cityRecords = [];
      this.loadingCities = false;
    };

    if (this.isSimulatorProfile) {
      const organizationId = this.userService?.currentOrganization?.id;
      this.projectsService.getMyInvitedProjects(organizationId, handleResponse, handleError);
      return;
    }

    this.backend.get('nerf-simulator-records').subscribe({
      next: handleResponse,
      error: handleError,
    });
  }

  /**
   * Seleziona automaticamente il centro contrassegnato come default (select_default = 1)
   * e ne carica i dati nel form. Se non esiste, lascia lo stato azzerato/disabilitato.
   */
  private selectDefaultCity(): void {
    const defaultRecord = this.cityRecords.find((record: any) => Number(record?.select_default) === 1);
    if (!defaultRecord) {
      return;
    }

    // setValue con emitEvent:false aggiorna la combo senza far scattare (selectionChange).
    this.cityControl.setValue(defaultRecord.id, { emitEvent: false });
    this.loadCenter(defaultRecord);
  }

  /**
   * Cambio centro dalla combo: carica i dati del centro selezionato.
   */
  onCitySelected(recordId: number | null): void {
    const record = this.cityRecords.find((item: any) => item.id === recordId) ?? null;
    // Cambiando centro, la tabella torna a filtrare sul centro selezionato.
    this.showAllSimulations = false;
    this.pruneSelectionToDisplayed();
    this.loadCenter(record);
  }

  /** Filtra le opzioni della combo in base al testo di ricerca (case-insensitive). */
  onCitySearch(term: string): void {
    const search = (term ?? '').toLowerCase().trim();
    this.filteredCityRecords = search
      ? this.cityRecords.filter((record: any) => (record.city ?? '').toLowerCase().includes(search))
      : [...this.cityRecords];
  }

  /** All'apertura del pannello ripristina l'elenco completo e mette il focus sulla ricerca. */
  onCityPanelOpenedChange(opened: boolean): void {
    if (opened) {
      this.filteredCityRecords = [...this.cityRecords];
      // Il pannello è in un overlay: attendo il render prima di azzerare e focalizzare l'input.
      setTimeout(() => {
        const el = this.citySearchInput?.nativeElement;
        if (el) {
          el.value = '';
          el.focus();
        }
      });
    }
  }

  /**
   * Carica nel form i dati del centro selezionato, oppure ripristina lo stato
   * azzerato/disabilitato se non c'è un centro valido.
   */
  private loadCenter(record: any): void {
    // input_data può arrivare come oggetto (cast Eloquent) o come stringa JSON: gestiamo entrambi.
    let inputData: any = record?.input_data ?? null;
    if (typeof inputData === 'string') {
      try {
        inputData = JSON.parse(inputData);
      } catch {
        inputData = null;
      }
    }

    if (!record || !inputData || typeof inputData !== 'object') {
      // Gli hospital (project) delle org simulator non hanno input_data da cui fare
      // prefill (non sono nerf-simulator-records): invece di lasciare il form
      // disabilitato/azzerato, lo abilitiamo con i valori di default esistenti.
      if (record && this.isSimulatorProfile) {
        this.simulatorForm.enable({ emitEvent: false });
        this.applyFixedFieldState();
        this.simulatorForm.patchValue({ ...this.administrationsPerYearDefaults, ...this.editableParamDefaults });
        this.simulationResult = null;
        this.hasResult = false;
        this.currentSimulationId = null;
        this.isSimulationSaved = false;
        return;
      }
      this.resetAllToZeroAndDisable();
      return;
    }

    // Abilita il form, poi ripristina i campi fissi come read-only.
    this.simulatorForm.enable({ emitEvent: false });
    this.applyFixedFieldState();

    // Popola con i dati del centro (composizioni/Safety/No-Show da frazione a percentuale).
    // Somministrazioni annue: valori di default sotto a quelli del centro, così un centro
    // che non le ha ancora non eredita i valori del centro selezionato prima.
    this.simulatorForm.patchValue({ ...this.administrationsPerYearDefaults, ...this.toPercentageParams(inputData) });

    // Reset dello stato "salvato" perché stiamo caricando nuovi parametri.
    this.simulationResult = null;
    this.hasResult = false;
    this.currentSimulationId = null;
    this.isSimulationSaved = false;
  }

  /**
   * Converte i valori memorizzati come frazione (0-1) in percentuale (0-100) per la UI.
   */
  private toPercentageParams(source: any): any {
    const params = { ...source };
    params.ocrelizumab_subcutaneous_composition = (params.ocrelizumab_subcutaneous_composition || 0) * 100;
    params.ocrelizumab_intravenous_composition = (params.ocrelizumab_intravenous_composition || 0) * 100;
    params.ublituximab_composition = (params.ublituximab_composition || 0) * 100;
    params.natalizumab_intravenous_composition = (params.natalizumab_intravenous_composition || 0) * 100;
    params.natalizumab_subcutaneous_composition = (params.natalizumab_subcutaneous_composition || 0) * 100;
    params.Safety_Buffer = (params.Safety_Buffer || 0) * 100;
    params.No_Show_Rate = (params.No_Show_Rate ?? 0) * 100;
    return params;
  }

  /** Controlli composizione (percentuali) che concorrono alla somma = 100%. */
  private readonly compositionControls: ReadonlyArray<string> = [
    'ocrelizumab_subcutaneous_composition',
    'ocrelizumab_intravenous_composition',
    'ublituximab_composition',
    'natalizumab_intravenous_composition',
    'natalizumab_subcutaneous_composition',
  ];

  /** Nomi brevi dei farmaci per il riepilogo del mix nello storico (ordine della scheda parametri). */
  private readonly compositionShortNames: ReadonlyArray<[string, string]> = [
    ['ublituximab_composition', 'Ublituximab'],
    ['ocrelizumab_intravenous_composition', 'Ocrelizumab EV'],
    ['ocrelizumab_subcutaneous_composition', 'Ocrelizumab SC'],
    ['natalizumab_intravenous_composition', 'Natalizumab EV'],
    ['natalizumab_subcutaneous_composition', 'Natalizumab SC'],
  ];

  onSubmit(): void {
    if (this.simulatorForm.valid) {
      this.loading = true;
      this.hasResult = false;

      // Use getRawValue() to include disabled fields
      const formData = this.simulatorForm.getRawValue();

      // Convert percentage values to decimals for backend
      formData.ocrelizumab_subcutaneous_composition = formData.ocrelizumab_subcutaneous_composition / 100;
      formData.ocrelizumab_intravenous_composition = formData.ocrelizumab_intravenous_composition / 100;
      formData.ublituximab_composition = formData.ublituximab_composition / 100;
      formData.natalizumab_intravenous_composition = formData.natalizumab_intravenous_composition / 100;
      formData.natalizumab_subcutaneous_composition = formData.natalizumab_subcutaneous_composition / 100;
      formData.Safety_Buffer = formData.Safety_Buffer / 100;
      formData.No_Show_Rate = formData.No_Show_Rate / 100;

      this.backend.post(`nerf-simulator/${this.projectId}`, formData).subscribe({
        next: (result: any) => {
          // console.log('NERF Simulator Response:', result);
          // console.log('Response type:', typeof result);

          let parsedResult = result;

          // Check if response has Lambda format (statusCode and body)
          if (result && result.statusCode && result.body) {
            // console.log('Lambda response detected, extracting body...');
            try {
              // Parse the body string
              parsedResult = JSON.parse(result.body);
              // console.log('Parsed body:', parsedResult);
            } catch (e) {
              console.error('Error parsing Lambda body:', e);
              this.backend.showErrors({ message: 'Invalid JSON in Lambda response body' });
              this.loading = false;
              return;
            }
          } else if (typeof result === 'string') {
            // Handle plain string response
            // console.log('Response is a string, parsing JSON...');
            try {
              parsedResult = JSON.parse(result);
              // console.log('Parsed result:', parsedResult);
            } catch (e) {
              console.error('Error parsing JSON:', e);
              this.backend.showErrors({ message: 'Invalid JSON response from server' });
              this.loading = false;
              return;
            }
          }

          // console.log('Final result keys:', Object.keys(parsedResult));
          // console.log('Final result:', parsedResult);
          this.simulationResult = parsedResult;
          this.hasResult = true;
          this.loading = false;
          this.isSimulationSaved = false;
          this.currentSimulationId = null; // Reset ID for new simulation
          this.currentSimulationCreatedAt = new Date().toISOString();

          // Automatically save the simulation to database
          // console.log('Auto-save triggered after simulation');
          this.saveSimulation();
        },
        error: (error: any) => {
          console.error('Error running NERF simulation:', error);
          this.backend.showErrors(error);
          this.loading = false;
        }
      });
    }
  }

  onReset(): void {
    const currentRaw = this.simulatorForm.getRawValue() as Record<string, number>;
    this.simulatorForm.reset({
      ...currentRaw,
      ...this.editableParamDefaults
    });
    this.hasResult = false;
    this.simulationResult = null;
    this.currentSimulationId = null;
    this.currentSimulationCreatedAt = null;
    this.isSimulationSaved = false;
  }

  saveSimulation(): void {
    if (!this.simulationResult) {
      this.backend.showErrors({ message: 'No simulation result to save' });
      return;
    }

    // Don't save if already saved
    if (this.isSimulationSaved) {
      // console.log('Simulation already saved, skipping save operation');
      return;
    }

    this.savingSimulation = true;

    // Convert percentage values to decimals for backend before saving
    const formData = this.simulatorForm.getRawValue();
    const inputParams = {
      ...formData,
      ocrelizumab_subcutaneous_composition: formData.ocrelizumab_subcutaneous_composition / 100,
      ocrelizumab_intravenous_composition: formData.ocrelizumab_intravenous_composition / 100,
      ublituximab_composition: formData.ublituximab_composition / 100,
      natalizumab_intravenous_composition: formData.natalizumab_intravenous_composition / 100,
      natalizumab_subcutaneous_composition: formData.natalizumab_subcutaneous_composition / 100,
      Safety_Buffer: formData.Safety_Buffer / 100,
      No_Show_Rate: formData.No_Show_Rate / 100
    };

    // Centro di riferimento selezionato (per ricaricarlo insieme ai dati).
    const selectedCityId = this.cityControl.value;
    const selectedCity = this.cityRecords.find((record: any) => record.id === selectedCityId);

    const simulationData = {
      project_id: this.projectId,
      city: selectedCity?.city ?? null,
      city_record_id: selectedCityId ?? null,
      input_parameters: inputParams,
      output_results: this.simulationResult
    };

    this.backend.post('nerf-simulations/save', simulationData).subscribe({
      next: (response: any) => {
        // console.log('Simulation saved successfully:', response);
        this.currentSimulationId = response.simulation_id || response.id;
        this.currentSimulationCreatedAt = response.created_at || response.createdAt || this.currentSimulationCreatedAt;
        this.isSimulationSaved = true;
        this.savingSimulation = false;
        this.loadSimulationHistory();
      },
      error: (error: any) => {
        console.error('Error saving simulation:', error);
        this.backend.showErrors(error);
        this.savingSimulation = false;
        this.isSimulationSaved = false;
      }
    });
  }

  loadSimulationHistory(): void {
    if (!this.projectId) {
      return;
    }

    this.loadingHistory = true;

    this.backend.get(`nerf-simulations/list/${this.projectId}`).subscribe({
      next: (response: any) => {
        // console.log('Simulation history:', response);
        const simulations = response.simulations || response.data || response;
        // Riepilogo del mix calcolato una volta sola, mostrato sotto il centro nella tabella.
        this.simulationHistory = Array.isArray(simulations)
          ? simulations.map((simulation: any) => ({
              ...simulation,
              composition_summary: this.compositionSummary(simulation.input_parameters),
            }))
          : simulations;
        if (this.currentSimulationId) {
          const selectedSimulation = this.simulationHistory.find((simulation: any) => simulation.id === this.currentSimulationId);
          if (selectedSimulation?.created_at) {
            this.currentSimulationCreatedAt = selectedSimulation.created_at;
          }
        }
        this.loadingHistory = false;
      },
      error: (error: any) => {
        console.error('Error loading simulation history:', error);
        this.simulationHistory = [];
        this.loadingHistory = false;
      }
    });
  }

  /** input_parameters salvati: oggetto (cast Eloquent) o stringa JSON; null se non leggibili. */
  private parseInputParameters(value: any): any {
    let params = value;
    if (typeof params === 'string') {
      try {
        params = JSON.parse(params);
      } catch {
        return null;
      }
    }
    return params && typeof params === 'object' ? params : null;
  }

  /**
   * Mix salvato (frazioni 0–1) come "Ublituximab 20%, Ocrelizumab EV 60%, ...":
   * omette i farmaci a 0 e arrotonda a un decimale.
   */
  private compositionSummary(inputParameters: any): string {
    const params = this.parseInputParameters(inputParameters);
    if (!params) {
      return '';
    }

    return this.compositionShortNames
      .map(([key, name]) => ({ name, percent: Math.round((Number(params[key]) || 0) * 1000) / 10 }))
      .filter((item) => item.percent > 0)
      .map((item) => `${item.name} ${item.percent}%`)
      .join(', ');
  }

  // ---- Selezione simulazioni + generazione report (SurveyQuestionSummary) ----

  /**
   * Simulazioni mostrate in tabella: solo quelle del centro selezionato, a meno
   * che l'utente non abbia scelto di vederle tutte (showAllSimulations).
   */
  get displayedSimulationHistory(): any[] {
    if (this.showAllSimulations) {
      return this.simulationHistory;
    }

    const record = this.cityRecords.find((item: any) => item.id === this.cityControl.value);
    if (!record) {
      // Nessun centro selezionato: mostra tutto.
      return this.simulationHistory;
    }

    return this.simulationHistory.filter((simulation: any) =>
      (simulation.city_record_id != null && simulation.city_record_id === record.id)
      || (simulation.city != null && record.city != null && simulation.city === record.city)
    );
  }

  /** Mostra/nasconde tutte le simulazioni (ignorando il filtro per centro). */
  toggleShowAllSimulations(): void {
    this.showAllSimulations = !this.showAllSimulations;
    // Le righe ora non più visibili non devono restare selezionate.
    this.pruneSelectionToDisplayed();
  }

  /** Rimuove dalla selezione gli id non più presenti tra le righe mostrate. */
  private pruneSelectionToDisplayed(): void {
    const visibleIds = new Set(this.displayedSimulationHistory.map((simulation: any) => simulation.id));
    for (const id of Array.from(this.selectedSimulationIds)) {
      if (!visibleIds.has(id)) {
        this.selectedSimulationIds.delete(id);
      }
    }
  }

  /** True se la simulazione è spuntata. */
  isSimulationSelected(simulation: any): boolean {
    return this.selectedSimulationIds.has(simulation?.id);
  }

  /** Toggle della singola riga. */
  toggleSimulationSelection(simulation: any, checked: boolean): void {
    if (simulation?.id == null) {
      return;
    }
    if (checked) {
      this.selectedSimulationIds.add(simulation.id);
    } else {
      this.selectedSimulationIds.delete(simulation.id);
    }
  }

  /** True se tutte le righe visibili sono selezionate. */
  get allSimulationsSelected(): boolean {
    const displayed = this.displayedSimulationHistory;
    return displayed.length > 0
      && displayed.every((simulation: any) => this.selectedSimulationIds.has(simulation.id));
  }

  /** True se solo una parte è selezionata (stato indeterminato dell'header). */
  get someSimulationsSelected(): boolean {
    return this.selectedSimulationIds.size > 0 && !this.allSimulationsSelected;
  }

  /** Select/deselect all sulle righe attualmente mostrate. */
  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.displayedSimulationHistory.forEach((simulation: any) => this.selectedSimulationIds.add(simulation.id));
    } else {
      this.displayedSimulationHistory.forEach((simulation: any) => this.selectedSimulationIds.delete(simulation.id));
    }
  }

  /**
   * Avvia la generazione del report AI sulle simulazioni selezionate.
   * Il backend crea il file S3, lancia la Step Function (agente
   * SurveyQuestionSummary) e traccia lo stato; qui poi facciamo polling.
   */
  generateReport(): void {
    const simulationIds = Array.from(this.selectedSimulationIds);
    if (simulationIds.length === 0 || !this.projectId) {
      return;
    }

    this.generatingReport = true;
    this.currentReport = null;

    this.backend.post('nerf-simulations/report/generate', {
      project_id: this.projectId,
      simulation_ids: simulationIds,
    }).subscribe({
      next: (response: any) => {
        this.currentReport = response?.report ?? null;
        const reportId = response?.report_id ?? response?.report?.id ?? null;
        if (reportId) {
          this.pollReportStatus(reportId);
        } else {
          this.generatingReport = false;
        }
      },
      error: (error: any) => {
        console.error('Error starting nerf report:', error);
        this.backend.showErrors(error);
        this.generatingReport = false;
      }
    });
  }

  /** Polling dello stato del report finché non è completed/failed. */
  private pollReportStatus(reportId: number): void {
    this.clearReportPoll();
    this.reportPollTimer = setInterval(() => {
      this.backend.get(`nerf-simulations/report/${reportId}/status`).subscribe({
        next: (response: any) => {
          const report = response?.report ?? null;
          this.currentReport = report;
          const status = report?.status;
          if (status === 'completed' || status === 'failed') {
            this.clearReportPoll();
            this.generatingReport = false;
          }
        },
        error: (error: any) => {
          console.error('Error polling nerf report status:', error);
          this.clearReportPoll();
          this.generatingReport = false;
        }
      });
    }, 5000);
  }

  private clearReportPoll(): void {
    if (this.reportPollTimer) {
      clearInterval(this.reportPollTimer);
      this.reportPollTimer = null;
    }
  }

  /** Testo del report completato (report_result.text), se presente. */
  get reportResultText(): string | null {
    return this.currentReport?.report_result?.text ?? null;
  }

  /** Analisi molto breve prodotta dall'agente super_shortener (short_result.text). */
  get reportShortText(): string | null {
    return this.currentReport?.short_result?.text ?? null;
  }

  /**
   * Scarica il report generato in formato Word (.doc): il markdown viene convertito in HTML
   * e incapsulato in un documento HTML compatibile con Microsoft Word.
   */
  downloadReportWord(): void {
    const text = this.reportShortText;
    if (!text) {
      this.backend.showErrors({ message: 'No report to download' });
      return;
    }

    const bodyHtml = marked.parse(text, { async: false }) as string;
    const title = 'NERF Simulator - Report';
    const html =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
      'xmlns:w="urn:schemas-microsoft-com:office:word" ' +
      'xmlns="http://www.w3.org/TR/REC-html40">' +
      '<head><meta charset="utf-8"><title>' + title + '</title>' +
      '<style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt;} table{border-collapse:collapse;} ' +
      'td,th{border:1px solid #999;padding:4px;}</style></head>' +
      '<body>' + bodyHtml + '</body></html>';

    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const reportId = this.currentReport?.id ?? 'current';
    saveAs(blob, `nerf-report-${reportId}.doc`);
  }

  loadSimulation(simulation: any): void {
    if (!simulation || !simulation.input_parameters) {
      return;
    }

    // Enable the form (default state is disabled until a selection is made), then keep
    // the fixed fields read-only.
    this.simulatorForm.enable({ emitEvent: false });
    this.applyFixedFieldState();

    // Convert decimal values from backend to percentages for display and load into the form.
    this.simulatorForm.patchValue(this.toPercentageParams(simulation.input_parameters));

    // Ripristina il centro di riferimento nella combo (senza emettere selectionChange,
    // per non ricaricare i dati originali del centro sopra quelli salvati).
    this.cityControl.setValue(simulation.city_record_id ?? null, { emitEvent: false });

    // Load results
    this.simulationResult = simulation.output_results;
    this.hasResult = true;
    this.currentSimulationId = simulation.id;
    this.currentSimulationCreatedAt = simulation.created_at || null;
    this.isSimulationSaved = true; // This simulation is already saved

    // Scroll al pannello risultati (colonna destra; su schermi stretti è sotto le schede)
    setTimeout(() => {
      document.getElementById('nerf-simulation-results-anchor')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 150);
  }

  exportToExcel(simulationId?: number): void {
    const idToExport = simulationId || this.currentSimulationId;

    if (!idToExport) {
      this.backend.showErrors({ message: 'No simulation to export' });
      return;
    }

    // console.log('Starting Excel export for simulation ID:', idToExport);

    // Use getBlob method from BackendService
    this.backend.getBlob(`nerf-simulations/export/${idToExport}`).subscribe({
      next: (response: any) => {
        // console.log('Excel export response received:', response);

        // Extract the blob from the response
        const blob = response.body || response;

        // Get filename from Content-Disposition header or use default
        let filename = `nerf-simulation-${idToExport}.xlsx`;
        const contentDisposition = response.headers?.get('Content-Disposition');
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        // Create a download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // console.log('Excel file downloaded successfully');
      },
      error: (error: any) => {
        console.error('Error downloading Excel file from backend:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);

        // Fallback to CSV export if backend fails
        // console.log('Attempting CSV export as fallback...');
        this.exportToCSV(idToExport);
      }
    });
  }

  exportToCSV(simulationId?: number): void {
    // Find the simulation data
    let simulation: any;

    if (simulationId) {
      simulation = this.simulationHistory.find(s => s.id === simulationId);
    } else if (this.currentSimulationId) {
      simulation = this.simulationHistory.find(s => s.id === this.currentSimulationId);
    }

    // If not found in history, use current result
    if (!simulation && this.simulationResult) {
      const selectedCity = this.cityRecords.find((record: any) => record.id === this.cityControl.value);
      simulation = {
        id: this.currentSimulationId || 'current',
        city: selectedCity?.city ?? null,
        input_parameters: this.simulatorForm.getRawValue(),
        output_results: this.simulationResult,
        created_at: new Date().toISOString()
      };
    }

    if (!simulation) {
      this.backend.showErrors({ message: 'No simulation data to export' });
      return;
    }

    // Create CSV content
    let csvContent = 'NERF Simulator - Simulation Results\n\n';
    csvContent += `Simulation ID:,${simulation.id}\n`;
    csvContent += `Center:,${simulation.city || '-'}\n`;
    csvContent += `Date:,${new Date(simulation.created_at).toLocaleString()}\n\n`;

    // Input Parameters
    csvContent += 'INPUT PARAMETERS\n';
    csvContent += 'Parameter Name,Value,Unit\n';

    const inputParams = simulation.input_parameters;
    const paramUnits: any = {
      'ocrelizumab_subcutaneous_min': 'min',
      'ocrelizumab_subcutaneous_composition': '%',
      'ocrelizumab_intravenous_min': 'min',
      'ocrelizumab_intravenous_composition': '%',
      'ublituximab_min': 'min',
      'ublituximab_composition': '%',
      'natalizumab_intravenous_min': 'min',
      'natalizumab_subcutaneous_min': 'min',
      'natalizumab_intravenous_composition': '%',
      'natalizumab_subcutaneous_composition': '%',
      'Hours_Per_Day': 'h',
      'Gap_Min': 'min',
      'Working_Days_Month': 'days',
      'Avg_Infusion_Min_Scenario': 'min',
      'Safety_Buffer': '%',
      'No_Show_Rate': '%',
      'Gap_TimeHours_Per_Day': 'h',
      'Nurses_per_Shift': '',
      'Patients_per_Nurse': '',
      'avg_patients_per_chair_per_day': '',
      'Chairs_Active': '',
      'number_of_opening_days': 'days',
      'prep_time_chair_materials': 'min',
      'patient_reception_assessment_time': 'min',
      'vitals_premedication_setup_time': 'min',
      'post_infusion_admin_time': 'min',
      'avg_operational_gap_between_infusions': 'min',
      'infusion_service_hours': 'h'
    };

    for (const key in inputParams) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      csvContent += `${label},${inputParams[key]},${paramUnits[key] || ''}\n`;
    }

    // Output Results
    csvContent += '\nOUTPUT RESULTS\n';
    csvContent += 'Result Name,Value,Unit\n';

    const outputResults = simulation.output_results;
    const resultUnits: any = {
      'utilization_percent': '%',
      'patients_per_month': 'patients',
      'cycles_per_chair_day': 'cycles',
      'rotation_index': '',
      'effective_chairs': 'chairs',
      'nurse_capacity': '',
      'avg_infusion_time_mix': 'min'
    };

    for (const key in outputResults) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const value = typeof outputResults[key] === 'number' ? outputResults[key].toFixed(4) : outputResults[key];
      csvContent += `${label},${value},${resultUnits[key] || ''}\n`;
    }

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `nerf-simulation-${simulation.id}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // console.log('CSV export completed successfully');
  }

  handleDeleteClick(event: Event, simulationId: number): void {
    // Prevent all event propagation
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // console.log('=== DELETE BUTTON CLICKED ===');
    // console.log('Event prevented and stopped');

    // Call the actual delete function
    this.deleteSimulation(simulationId);
  }

  deleteSimulation(simulationId: number): void {
    if (!confirm('Are you sure you want to delete this simulation?')) {
      // console.log('Delete cancelled by user');
      return;
    }

    // console.log('=== DELETE OPERATION START ===');
    // console.log('Attempting to delete simulation ID:', simulationId);
    // console.log('Delete endpoint URL:', `nerf-simulations/${simulationId}`);
    // console.log('Using backend.destroy() method which sends HTTP DELETE');

    // Use destroy() method which actually does HTTP DELETE
    this.backend.destroy(`nerf-simulations/${simulationId}`).subscribe({
      next: (response: any) => {
        // console.log('=== DELETE SUCCESS ===');
        // console.log('Simulation deleted successfully:', response);

        // Remove from local history array immediately for better UX
        this.simulationHistory = this.simulationHistory.filter(s => s.id !== simulationId);
        // console.log('Removed from local array. New history count:', this.simulationHistory.length);

        // Clear current simulation if it was deleted
        if (this.currentSimulationId === simulationId) {
          // console.log('Clearing currently displayed simulation');
          this.currentSimulationId = null;
          this.currentSimulationCreatedAt = null;
          this.hasResult = false;
          this.simulationResult = null;
          this.isSimulationSaved = false;
        }

        // Reload history from server to sync
        // console.log('Reloading simulation history from server...');
        this.loadSimulationHistory();
      },
      error: (error: any) => {
        console.error('Error deleting simulation:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);
        console.error('Full error object:', error);

        // Check if it's a 404 or backend not implemented
        if (error?.status === 404) {
          alert('Delete endpoint not found. The backend endpoint may not be implemented yet.\n\nEndpoint: DELETE /api/nerf-simulations/' + simulationId);
        } else if (error?.status === 0) {
          alert('Cannot connect to backend. Please check if the Laravel server is running.');
        } else {
          this.backend.showErrors(error);
        }
      }
    });
  }

}
